import { DatePicker, Form, InputNumber, Modal } from '@maxtropy/components';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { PvEmissionRateRecord } from './types';

interface EditPvEmissionRateModalProps {
  open: boolean;
  record?: PvEmissionRateRecord;
  records: PvEmissionRateRecord[];
  onCancel: () => void;
  onOk: (values: PvEmissionRateRecord) => void;
}

interface ModalFormValues {
  startDate?: dayjs.Dayjs;
  rate?: number;
}

const EditPvEmissionRateModal = (props: EditPvEmissionRateModalProps) => {
  const { open, record, records, onCancel, onOk } = props;
  const [form] = Form.useForm<ModalFormValues>();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const isEdit = !!record;

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      startDate: record?.startDate ? dayjs(record.startDate) : undefined,
      rate: record?.rate,
    });
  }, [form, open, record]);

  const handleOk = async () => {
    setConfirmLoading(true);
    const values = await form
      .validateFields()
      .then(res => res)
      .catch(() => null);
    setConfirmLoading(false);

    if (!values?.startDate || values.rate === undefined) {
      return;
    }

    onOk({
      id: record?.id ?? Date.now(),
      endDate: record?.endDate,
      startDate: isEdit ? record.startDate : values.startDate.format('YYYY-MM-DD'),
      rate: values.rate,
    });
  };

  return (
    <Modal
      title={record ? '编辑光伏减排费率区间' : '新增光伏减排费率区间'}
      open={open}
      width={620}
      contentClassName="modal-form-content"
      destroyOnClose
      confirmLoading={confirmLoading}
      onCancel={onCancel}
      onOk={handleOk}
    >
      <Form form={form} layout="horizontal" labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
        <Form.Item
          label="开始时间"
          name="startDate"
          rules={[
            { required: true, message: '请选择' },
            {
              validator: (_, value?: dayjs.Dayjs) => {
                if (isEdit || !value) {
                  return Promise.resolve();
                }

                const hasSameStartDate = records.some(item => dayjs(item.startDate).isSame(value, 'day'));
                return hasSameStartDate ? Promise.reject(new Error('开始时间不能重复')) : Promise.resolve();
              },
            },
          ]}
        >
          <DatePicker disabled={isEdit} placeholder="请选择" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="费率"
          name="rate"
          rules={[
            { required: true, message: '请输入' },
            {
              type: 'number',
              min: 0,
              max: 99999999.99,
              message: '请输入0~99999999.99',
            },
          ]}
        >
          <InputNumber
            placeholder="请输入"
            addonAfter="元/kgCO2e"
            style={{ width: '100%' }}
            min={0}
            max={99999999.99}
            precision={2}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditPvEmissionRateModal;
